import React, { useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import taskService from '../services/taskService';
import { normalizeActivityStatus, parseActivityDate } from '../utils/taskActivityModel';

export default function TaskQuickProgressDialog({ task, onClose, onSaved }) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [status, setStatus] = useState(normalizeActivityStatus(task.status));
  const [expectedDate, setExpectedDate] = useState(() => {
    const date = parseActivityDate(task.due_date);
    return date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
  });
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const commentRef = useRef(null);
  const save = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    if (!comment.trim()) { setError('Escribe qué avanzó, qué falta o por qué está detenido.'); commentRef.current?.focus(); return; }
    inFlight.current = true;
    setBusy(true); setError('');
    try {
      const result = await taskService.recordProgress(task.id, { status, expected_date: expectedDate || null, comment: comment.trim(), expected_revision: task._edit_revision });
      onSaved(result);
      onClose();
    } catch (failure) {
      setError(failure.response?.data?.message || 'No se pudo guardar el avance. Tu comentario sigue aquí; puedes reintentar.');
    } finally { inFlight.current = false; setBusy(false); }
  };
  return <Dialog open onClose={() => { if (!inFlight.current) onClose(); }} fullScreen={mobile} fullWidth maxWidth="sm" aria-labelledby="quick-progress-title" PaperProps={{ sx: { width: mobile ? '100%' : 'calc(100% - 64px)', maxWidth: mobile ? '100%' : 600, pt: 'env(safe-area-inset-top, 0px)', pb: 'env(safe-area-inset-bottom, 0px)' } }}>
    <Box component="form" onSubmit={save} aria-busy={busy} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: mobile ? '100%' : 'auto' }}>
      <DialogTitle id="quick-progress-title">Registrar avance · #{task.id}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography fontWeight={750} sx={{ overflowWrap: 'anywhere' }}>{task.title}</Typography>
          <Typography variant="body2" color="text.secondary">El estatus, la fecha y tu comentario quedarán juntos en el historial.</Typography>
          {error && <Alert severity="error" role="alert">{error}</Alert>}
          <TextField select label="Nuevo estatus" value={status} disabled={busy} onChange={(event) => setStatus(event.target.value)} fullWidth>
            {[['todo', 'Pendiente'], ['in_progress', 'En curso'], ['blocked', 'En espera / bloqueado'], ['done', 'Terminado']].map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField>
          <TextField label="Fecha expectativa" type="date" value={expectedDate} disabled={busy} onChange={(event) => setExpectedDate(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: '1900-01-01', max: '9999-12-31' } }} sx={{ colorScheme: theme.palette.mode }} fullWidth helperText="Puedes conservarla, cambiarla o dejarla sin fecha." />
          <TextField label="Comentario del avance" autoFocus inputRef={commentRef} value={comment} disabled={busy} onChange={(event) => setComment(event.target.value)} multiline minRows={4} required fullWidth helperText="Describe el resultado y el siguiente paso." />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={busy} sx={{ minHeight: 44 }}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={busy || !comment.trim()} startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null} sx={{ minHeight: 44 }}>{busy ? 'Guardando…' : 'Guardar avance'}</Button>
      </DialogActions>
    </Box>
  </Dialog>;
}
